/**
 * voice-v8 — Werkzeugkarten: Rechnung und Belege (Beleg analysiert, Beleg gebucht).
 *
 * Herausgelöst aus lib/tool-cards.ts (15.10.2026, Plan „Karten zu 100 % einheitlich“, Welle 3) — Verhalten unverändert;
 * der eine Einstieg bleibt `buildToolCard` in lib/tool-cards.ts.
 */
import { type RechnungsKarte, belegArt, euro } from "../rechnung-karte.js";
import { absatz, aktionsKnopf, aktionsLeiste, baueKarte, betragZeile, chips, fehlerBlock, kartenTitel, wertZeile } from "../karte.js";
import { oeffnePdf } from "../pdf-schau.js";
import { unwrapResult } from "./hilfe.js";

/**
 * Die Rechnungskarte. (05.10.2026, Ticket 2996)
 *
 * Martin: nach dem Anlegen oder Ändern ein Knopf „Rechnung öffnen“ statt
 * eines nackten Links. Was die Karte zeigt, liest lib/rechnung-karte.ts aus
 * der Antwort des Werkzeugs; hier steht nur der Bau.
 *
 * Die Hauptsache ist der Betrag (DESIGN-NORM B7) — er steht groß und allein
 * unter der Nummer, wie auf der Rechnung selbst. Kunde und Firma sind leise
 * Zeilen darunter, die zwei Wege stehen in EINER Reihe (B4).
 *
 * ⚠️ Kein eigener Stil: Betrag und Netto-Zeile tragen die Klassen der
 *    Beleg-Karte, der Stand die der Chips, die Knöpfe das eine
 *    Knopf-System. Eine neue Regel neben den alten wäre die nächste Stelle,
 *    die auseinanderläuft.
 * ⚠️ Eine ACHTUNG-Zeile des Werkzeugs (falsche Firma, fehlender Rabatt)
 *    steht ZUERST in der Karte. Die Textkarte davor zeigte sie — eine
 *    hübsche Karte, die sie verschluckt, wäre ein Rückschritt.
 */
export function buildRechnungCard(k: RechnungsKarte): HTMLElement {
  const el = baueKarte("rechnung");
  el.dataset.rechnung = String(k.id);
  const name = k.nummer ?? `Nr. ${k.id}`;
  // Eine Gutschrift (GS-…) ist keine Rechnung — Titel, Namen der Knöpfe und PDF nennen sie so.
  const beleg = belegArt(k.nummer);

  // „Rechnung “ ist die Art (in einer Faltung nennt sie der Kopf), die Nummer der Titel.
  el.appendChild(kartenTitel(name, beleg));

  for (const w of k.warnungen) {
    const warn = absatz(w, "warn");
    // Korrekturrunde 2996: „note“, nicht „alert“. Die Karte wird beim Laden des
    // Verlaufs neu gebaut und in einem Zug mehrfach ERSETZT (je Rechnung eine
    // Karte) — „alert“ sagte dieselbe Warnung jedes Mal wieder an. Gesagt wird
    // sie schon: sie steht in der ersten Zeile, aus der Vespera antwortet.
    warn.setAttribute("role", "note");
    warn.setAttribute("aria-label", "Warnung");
    el.appendChild(warn);
  }

  if (k.brutto !== null) {
    const netto = [k.netto !== null ? `Netto ${euro(k.netto)}` : "", k.summeOffen ? "nicht bestätigt" : ""].filter(Boolean);
    betragZeile(el, euro(k.brutto), netto.length ? netto.join(" · ") : undefined);
  }

  // EINE Pille für jeden Stand — das Wort sagt, welcher es ist.
  if (k.status) chips(el, [k.status]);

  if (k.kunde) wertZeile(el, "Kunde", k.kunde);
  if (k.firma) wertZeile(el, "Firma", k.firma);

  const reihe = aktionsLeiste(el);
  reihe.appendChild(
    aktionsKnopf({
      text: "Rechnung öffnen",
      primaer: true,
      symbol: "external",
      symbolHinten: true,
      href: k.link,
      neuesFenster: true,
      // Der Name BEGINNT mit der sichtbaren Beschriftung (wer „Rechnung öffnen“
      // sagt, trifft den Knopf), dann welche — stehen mehrere im Zug — und wohin:
      // das neue Fenster sagte bisher niemand an.
      name: `Rechnung öffnen: ${beleg === "Rechnung" ? "" : `${beleg} `}${name} (Panel, neues Fenster)`,
    }),
  );
  reihe.appendChild(
    aktionsKnopf({
      text: "PDF ansehen",
      href: k.pdf,
      name: `PDF ansehen: ${beleg} ${name}`,
      // Wie pdfKnopf (app.ts): der einfache Klick bleibt in der App, Strg/Cmd/
      // Mittelklick behalten den Weg nach draußen.
      onClick: (ev) => {
        if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.button > 0) return;
        ev.preventDefault();
        oeffnePdf(k.pdf, `${beleg} ${name}`);
      },
    }),
  );
  return el;
}

// Pre.527: Receipt-Analyze-Card. Statt generischem JSON-Dump:
//   - Vendor-Header + Datum + Brutto prominent
//   - bis zu 3 Konto-Suggestion-Buttons mit Confidence-Bar
//   - „Anderes Konto…" + „Nicht verbuchen" Aktionen
//   - Warnings als kleine Hinweise unten
// Click auf Button schickt Text-Message an Bot, der dann expense_create
// mit den extrahierten Daten + gewähltem account_id ruft.
interface ReceiptAnalyzeResultShape {
  ok?: boolean;
  upload_id?: string;
  upload_url?: string;
  extracted?: {
    amount_gross?: number | null;
    amount_net?: number | null;
    tax_amount?: number | null;
    tax_rate?: number | null;
    currency?: string;
    date?: string | null;
    vendor?: string | null;
    vendor_address?: string | null;
    vendor_uid?: string | null;
    invoice_number?: string | null;
    payment_method?: string;
  };
  suggestions?: Array<{
    account_id: number;
    account_number: string;
    account_name: string;
    confidence: number;
    reason: string;
  }>;
  available_accounts?: Array<{
    id: number;
    number: string;
    name: string;
    type?: string;
    tax_rate?: number;
  }>;
  warnings?: string[];
  model?: string;
  error?: string;
}

export function buildReceiptAnalyzeCard(result: unknown): HTMLElement {
  const el = baueKarte("receipt");
  const raw = unwrapResult(result) as ReceiptAnalyzeResultShape;

  if (raw.ok === false || (typeof raw.error === "string" && raw.error)) {
    el.appendChild(kartenTitel("Beleg-Analyse"));
    el.appendChild(
      fehlerBlock(
        typeof raw.error === "string" ? raw.error : "Analyse fehlgeschlagen",
      ),
    );
    return el;
  }

  const ex = raw.extracted ?? {};
  const fmtEur = (v?: number | null): string =>
    typeof v === "number" ? `${v.toFixed(2)} €` : "—";

  // Pre.530: Thumbnail des verarbeiteten Belegs direkt in der Card.
  // upload_url kommt aus dem Tool-Result, Format /uploads/{userId}/{file}.
  if (raw.upload_url) {
    const thumbWrap = document.createElement("a");
    thumbWrap.className = "receipt-card-thumb";
    thumbWrap.href = raw.upload_url;
    thumbWrap.target = "_blank";
    thumbWrap.rel = "noopener";
    const thumb = document.createElement("img");
    thumb.src = raw.upload_url;
    thumb.alt = "Beleg-Vorschau";
    thumb.loading = "lazy";
    thumbWrap.appendChild(thumb);
    el.appendChild(thumbWrap);
  }

  // Header: Vendor + Datum + Betrag
  const head = document.createElement("div");
  head.className = "receipt-card-head";

  const vendorEl = document.createElement("div");
  vendorEl.className = "receipt-card-vendor";
  vendorEl.textContent = ex.vendor ?? "Unbekannter Vendor";
  head.appendChild(vendorEl);

  const metaRow = document.createElement("div");
  metaRow.className = "receipt-card-meta";
  const metaParts: string[] = [];
  if (ex.date) metaParts.push(ex.date);
  if (ex.invoice_number) metaParts.push(`Nr. ${ex.invoice_number}`);
  if (ex.payment_method && ex.payment_method !== "unknown") {
    const labels: Record<string, string> = {
      card: "Karte",
      cash: "Bar",
      bank: "Überweisung",
    };
    metaParts.push(labels[ex.payment_method] ?? ex.payment_method);
  }
  if (metaParts.length > 0) metaRow.textContent = metaParts.join(" · ");
  head.appendChild(metaRow);

  const taxRateStr =
    typeof ex.tax_rate === "number"
      ? ` (${ex.tax_rate}%)`
      : ex.tax_rate === null
        ? " (gemischt)"
        : "";
  betragZeile(
    head,
    fmtEur(ex.amount_gross),
    typeof ex.amount_net === "number" && typeof ex.tax_amount === "number"
      ? `Netto ${fmtEur(ex.amount_net)} + MwSt ${fmtEur(ex.tax_amount)}${taxRateStr}`
      : undefined,
  );

  el.appendChild(head);

  // Suggestions als Buttons
  const suggestions = Array.isArray(raw.suggestions) ? raw.suggestions : [];
  if (suggestions.length > 0) {
    const sugBlock = document.createElement("div");
    sugBlock.className = "receipt-card-suggestions";

    const sugLabel = document.createElement("div");
    sugLabel.className = "receipt-card-sug-label";
    sugLabel.textContent = "Vorschlag — Konto auswählen:";
    sugBlock.appendChild(sugLabel);

    for (const s of suggestions) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "receipt-card-sug-btn";
      btn.dataset.accountId = String(s.account_id);
      btn.dataset.accountNumber = s.account_number;

      const top = document.createElement("div");
      top.className = "receipt-card-sug-top";
      const numEl = document.createElement("span");
      numEl.className = "receipt-card-sug-num";
      numEl.textContent = s.account_number;
      const nameEl = document.createElement("span");
      nameEl.className = "receipt-card-sug-name";
      nameEl.textContent = s.account_name;
      const confEl = document.createElement("span");
      confEl.className = "receipt-card-sug-conf";
      confEl.textContent = `${Math.round(s.confidence * 100)}%`;
      top.appendChild(numEl);
      top.appendChild(nameEl);
      top.appendChild(confEl);
      btn.appendChild(top);

      // Confidence-bar
      const bar = document.createElement("div");
      bar.className = "receipt-card-sug-bar";
      const fill = document.createElement("div");
      fill.className = "receipt-card-sug-bar-fill";
      fill.style.width = `${Math.max(2, Math.round(s.confidence * 100))}%`;
      bar.appendChild(fill);
      btn.appendChild(bar);

      const reason = document.createElement("div");
      reason.className = "receipt-card-sug-reason";
      reason.textContent = s.reason;
      btn.appendChild(reason);

      btn.addEventListener("click", () => {
        const send = (
          window as unknown as {
            __voiceV8SendFromCard?: (text: string) => Promise<void>;
          }
        ).__voiceV8SendFromCard;
        if (send) {
          void send(
            `Buche auf Konto ${s.account_number} ${s.account_name} (account_id ${s.account_id}).`,
          );
        }
        // Optisches Feedback: alle Buttons disablen
        sugBlock
          .querySelectorAll<HTMLButtonElement>(".receipt-card-sug-btn")
          .forEach((b) => {
            b.disabled = true;
            b.classList.toggle(
              "is-chosen",
              b.dataset.accountId === String(s.account_id),
            );
          });
      });
      sugBlock.appendChild(btn);
    }

    // „Anderes Konto" + „Nicht verbuchen" Aktion-Reihe
    const actionRow = document.createElement("div");
    actionRow.className = "receipt-card-actions";

    // Pre.528: Dropdown mit ALLEN Konten — reveal beim Click auf
    // „Anderes Konto…", Auswahl triggert dieselbe Buche-Logik wie die
    // großen Vorschlags-Buttons.
    const allAccounts = Array.isArray(raw.available_accounts)
      ? raw.available_accounts
      : [];
    const otherWrap = document.createElement("div");
    otherWrap.className = "receipt-card-other-wrap";

    const otherBtn = document.createElement("button");
    otherBtn.type = "button";
    otherBtn.className = "receipt-card-action-btn receipt-card-action-other";
    otherBtn.textContent =
      allAccounts.length > 0
        ? `Anderes Konto (${allAccounts.length})`
        : "Anderes Konto…";
    otherWrap.appendChild(otherBtn);

    if (allAccounts.length > 0) {
      // Native <select> — auf iOS/Android nativer Picker, einfach scrollbar.
      const select = document.createElement("select");
      select.className = "receipt-card-other-select";
      select.hidden = true;

      // Placeholder-Option
      const placeholder = document.createElement("option");
      placeholder.value = "";
      placeholder.textContent = "— Konto wählen —";
      placeholder.disabled = true;
      placeholder.selected = true;
      select.appendChild(placeholder);

      // Group nach type (Expense / Income / Asset / Liability)
      const byType = new Map<string, typeof allAccounts>();
      for (const a of allAccounts) {
        const k = a.type ?? "Andere";
        const arr = byType.get(k) ?? [];
        arr.push(a);
        byType.set(k, arr);
      }
      const typeOrder = ["Expense", "Income", "Asset", "Liability", "Andere"];
      for (const tk of typeOrder) {
        const list = byType.get(tk);
        if (!list || list.length === 0) continue;
        const group = document.createElement("optgroup");
        group.label =
          tk === "Expense"
            ? "Ausgaben"
            : tk === "Income"
              ? "Einnahmen"
              : tk === "Asset"
                ? "Vermögen"
                : tk === "Liability"
                  ? "Verbindlichkeiten"
                  : tk;
        // Innerhalb der Gruppe nach Konto-Nr sortiert
        list.sort((a, b) => a.number.localeCompare(b.number));
        for (const a of list) {
          const opt = document.createElement("option");
          opt.value = String(a.id);
          opt.dataset.number = a.number;
          opt.dataset.name = a.name;
          opt.textContent = `${a.number} — ${a.name}`;
          group.appendChild(opt);
        }
        select.appendChild(group);
      }

      otherBtn.addEventListener("click", () => {
        select.hidden = !select.hidden;
        if (!select.hidden) select.focus();
      });

      select.addEventListener("change", () => {
        const accId = Number(select.value);
        const chosen = allAccounts.find((a) => a.id === accId);
        if (!chosen) return;
        const send = (
          window as unknown as {
            __voiceV8SendFromCard?: (text: string) => Promise<void>;
          }
        ).__voiceV8SendFromCard;
        if (send) {
          void send(
            `Buche auf Konto ${chosen.number} ${chosen.name} (account_id ${chosen.id}).`,
          );
        }
        // Disable everything in this card
        sugBlock
          .querySelectorAll<HTMLButtonElement>(".receipt-card-sug-btn")
          .forEach((b) => {
            b.disabled = true;
          });
        select.disabled = true;
        otherBtn.disabled = true;
      });

      otherWrap.appendChild(select);
    } else {
      // Fallback ohne Liste: weiterhin Bot-Konversation
      otherBtn.addEventListener("click", () => {
        const send = (
          window as unknown as {
            __voiceV8SendFromCard?: (text: string) => Promise<void>;
          }
        ).__voiceV8SendFromCard;
        if (send)
          void send("Anderes Konto bitte — welche stehen noch zur Auswahl?");
      });
    }

    actionRow.appendChild(otherWrap);

    const skipBtn = document.createElement("button");
    skipBtn.type = "button";
    skipBtn.className = "receipt-card-action-btn receipt-card-action-skip";
    skipBtn.textContent = "Nicht verbuchen";
    skipBtn.addEventListener("click", () => {
      const send = (
        window as unknown as {
          __voiceV8SendFromCard?: (text: string) => Promise<void>;
        }
      ).__voiceV8SendFromCard;
      if (send) void send("Nein, nicht verbuchen.");
    });
    actionRow.appendChild(skipBtn);

    sugBlock.appendChild(actionRow);
    el.appendChild(sugBlock);
  }

  // Warnings
  const warnings = Array.isArray(raw.warnings) ? raw.warnings : [];
  if (warnings.length > 0) {
    const wBlock = document.createElement("div");
    wBlock.className = "receipt-card-warnings";
    const wLabel = document.createElement("div");
    wLabel.className = "receipt-card-warn-label";
    wLabel.textContent = "Hinweise";
    wBlock.appendChild(wLabel);
    for (const w of warnings) {
      const wItem = document.createElement("div");
      wItem.className = "receipt-card-warn-item";
      wItem.textContent = w;
      wBlock.appendChild(wItem);
    }
    el.appendChild(wBlock);
  }

  return el;
}

interface ReceiptBookedShape {
  ok?: boolean;
  vendor?: string | null;
  gross_amount?: number;
  account_number?: string;
  account_name?: string;
  expense_id?: number | null;
  invoice_id?: number | null;
  upload_url?: string;
  expense_date?: string | null;
}

/**
 * Pre.538: Kompakte „✓ Verbucht"-Card aus dem deterministischen Foto→Konto→
 * Fertig-Flow. Bewusst minimal: Beleg-Thumbnail + Vendor/Betrag + Zielkonto +
 * Beleg-Nr. Keine Vorschlagsliste, kein Dropdown — die Buchung ist erledigt.
 */
export function buildReceiptBookedCard(result: unknown): HTMLElement {
  const el = baueKarte("receipt-booked");
  const r = unwrapResult(result) as ReceiptBookedShape;
  const fmtEur = (v?: number | null): string =>
    typeof v === "number" ? `${v.toFixed(2)} €` : "—";

  if (r.upload_url) {
    const thumbWrap = document.createElement("a");
    thumbWrap.className = "receipt-card-thumb receipt-booked-thumb";
    thumbWrap.href = r.upload_url;
    thumbWrap.target = "_blank";
    thumbWrap.rel = "noopener";
    const thumb = document.createElement("img");
    thumb.src = r.upload_url;
    thumb.alt = "Beleg";
    thumb.loading = "lazy";
    thumbWrap.appendChild(thumb);
    el.appendChild(thumbWrap);
  }

  const head = document.createElement("div");
  head.className = "receipt-booked-head";

  const badge = document.createElement("div");
  badge.className = "receipt-booked-badge";
  // SVG-Häkchen statt Unicode-Glyph (Projekt-Regel: keine Emojis in UI).
  badge.innerHTML =
    '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">' +
    '<path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" ' +
    'stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
    "<span>Verbucht</span>";
  head.appendChild(badge);

  const vendorEl = document.createElement("div");
  vendorEl.className = "receipt-card-vendor";
  vendorEl.textContent = r.vendor ?? "Beleg";
  head.appendChild(vendorEl);

  betragZeile(head, fmtEur(r.gross_amount));

  const acct = document.createElement("div");
  acct.className = "receipt-booked-account";
  acct.textContent =
    `→ ${r.account_number ?? ""} ${r.account_name ?? ""}`.trim();
  head.appendChild(acct);

  const meta = document.createElement("div");
  meta.className = "receipt-card-meta";
  const parts: string[] = [];
  if (r.expense_date) parts.push(r.expense_date);
  if (r.invoice_id) parts.push(`Beleg #${r.invoice_id}`);
  if (parts.length > 0) meta.textContent = parts.join(" · ");
  head.appendChild(meta);

  el.appendChild(head);
  return el;
}
